<script lang="ts">
import {defineComponent, watch, type PropType, type WatchStopHandle} from 'vue';
import $ from 'jquery';
import {SvgIcon} from '../../svg.ts';
import DiffFilterTreeItem from './DiffFilterTreeItem.vue';
import {SCHEMA_TYPES, type SchemaType} from '../../features/allspicejson.ts';
import {buildGroupedChangesFilterTree, type FilterItem} from './diff-filter-tree.ts';
import {buildFilterDataFromContents, type AttributeMapState} from './diff-filter-data.ts';
import {determineSchemaType, parseDiffFilterTargetId} from './diff-filter-shared.ts';
import {
  applyStoredFilterOverrides,
  buildAttributeTree,
  buildStoredFilterOverrides,
  collectHiddenFilters,
  recalculateHiddenDataIds,
  setVisibilityRecursively,
} from './diff-filter-state.ts';
import {FRONTEND_RENDER_THRESHOLD} from './AllSpiceDiff.vue';
import {timing} from '../../analytics.ts';
import {useAllSpiceAppStore} from '../../allspice_app/store.ts';
import {saveDiffFilterState} from '../../allspice_app/diffFilterPersistence.ts';
import {formatGroupName, type TreeNodeWithPage} from '../../allspice_app/components/diff-list/diff-grouping.ts';
import {findPageContainer} from '../../allspice_app/components/diff-list/page-navigation.ts';

type DiffJsonNode = Record<string, unknown> | null | undefined;
type DiffJson = Record<string, unknown> & {
  pages?: DiffJsonNode[];
};
type GroupedChanges = Record<string, TreeNodeWithPage[]>;

type DiffFilterData = AttributeMapState & {
  emptyMessage: string;
  filesParsed: Set<string>;
  filterList: FilterItem[];
  hiddenDataIds: Set<string>;
  isLoading: boolean;
  boundOnInspectorDataLoad: (event: Event) => void;
  boundOnResync: () => void;
  diffFileBox: Element | null;
  stopFilterOverridesWatch: WatchStopHandle | null;
  boundOnSvgDataLoad: ((event: Event) => void) | null;
};

function getStringProperty(value: DiffJsonNode, key: string): string | null {
  const property = value?.[key];
  return typeof property === 'string' ? property : null;
}

export default defineComponent({
  name: 'DiffFilter',
  components: {SvgIcon, DiffFilterTreeItem},

  props: {
    // Raw file size in bytes. Used to skip processing for very large files.
    // Not available in the Vue app context (file size isn't in DiffTreeNode);
    // defaults to 0, which effectively disables the guard. This is acceptable
    // because AllSpiceJsonLoader would fail on truly huge files before we get here.
    headSize: {
      type: Number,
      default: 0,
    },
    headJson: {
      type: Object as PropType<DiffJson | null>,
      default: null,
    },
    baseJson: {
      type: Object as PropType<DiffJson | null>,
      default: null,
    },
    sidebar: {
      type: Boolean,
      default: false,
    },
    groupedChanges: {
      type: Object as PropType<GroupedChanges | null>,
      default: null,
    },
    schemaType: {
      type: String as PropType<SchemaType>,
      default: SCHEMA_TYPES.UNKNOWN,
    },
    pageNameMap: {
      type: Object as PropType<Record<string, string> | null>,
      default: null,
    },
    filteredGroupCounts: {
      type: Object as PropType<Record<string, number> | null>,
      default: null,
    },
    visibleGroupCounts: {
      type: Object as PropType<Record<string, number> | null>,
      default: null,
    },
  },

  data(): DiffFilterData {
    return {
      attributesToDataIds: {},
      dataIdsToAttributes: {},
      emptyMessage: '(No Changes)',
      filesParsed: new Set<string>(),
      filterList: [],
      hiddenDataIds: new Set<string>(),
      isLoading: false,
      boundOnInspectorDataLoad: (_event: Event) => undefined,
      boundOnResync: () => undefined,
      diffFileBox: null,
      stopFilterOverridesWatch: null,
      boundOnSvgDataLoad: null,
    };
  },

  computed: {
    diffJsonPair() {
      return [this.headJson, this.baseJson];
    },
    hasActiveOverrides() {
      if (this.filterList.length === 0) return false;
      return this.filterList.some((item) => !item.isVisible || item.isIndeterminate);
    },
  },

  watch: {
    hiddenDataIds: {
      handler() {
        const store = useAllSpiceAppStore();
        // Only the DOM-attached (active) instance should write to the store.
        // Detached instances (saved in preview.value.html during navigation)
        // must not overwrite the active file's hidden IDs.
        if (store && document.contains(this.$el)) {
          store.diffFilterHiddenIds.value = Array.from(this.hiddenDataIds);
        }
      },
      deep: true,
    },
    diffJsonPair: {
      handler() {
        if (this.groupedChanges) return;
        if (this.headJson || this.baseJson) {
          this.loadFilterDataFromJson(this.headJson, this.baseJson);
        }
      },
      immediate: true,
    },
    groupedChanges: {
      handler(val: GroupedChanges | null) {
        if (val) {
          this.buildFromGroupedChanges();
        }
      },
      immediate: true,
    },
    filteredGroupCounts() {
      this.updateGroupLabels();
    },
    visibleGroupCounts() {
      this.updateGroupLabels();
    },
  },

  mounted() {
    const $box = this.$el.closest('.diff-file-box');

    // Store bound handlers so we can remove them in beforeUnmount.
    this.boundOnInspectorDataLoad = this.onInspectorDataLoad.bind(this);
    this.boundOnResync = this.onResync.bind(this);
    this.diffFileBox = $box;

    if (!this.sidebar) {
      $box.addEventListener('inspectordataload', this.boundOnInspectorDataLoad);
      $(this.$refs.dropdown).dropdown({
        useLabels: false,
        action: 'nothing',
      });
    }

    if (this.headSize > FRONTEND_RENDER_THRESHOLD) {
      this.isLoading = false;
      this.emptyMessage = '(File too large)';
    }

    // In the project view, FileDiffPreview saves and reuses DOM elements
    // (including mounted Vue apps) across file navigations. When a file's
    // DOM is re-attached, inspectordataload does NOT re-fire, so we listen
    // for a resync event to re-apply stored overrides and update the store.
    if ($box) {
      $box.addEventListener('diff-state-resync', this.boundOnResync);
    }

    // In sidebar mode, buildFromGroupedChanges may run before or after mount
    // (groupedChanges can arrive later when diff data loads). Either way, the
    // SVG isn't in the DOM when applyDisplayStyle first runs. Listen for
    // inspectordataload (which fires after SVG content + data-id elements
    // are in the DOM) to re-apply display styles.
    if (this.sidebar) {
      this.boundOnSvgDataLoad = () => {
        if (this.hiddenDataIds.size === 0) return;
        this.applyDisplayStyle(Object.keys(this.dataIdsToAttributes), true);
        this.applyDisplayStyle(Array.from(this.hiddenDataIds), false);
      };
      document.addEventListener('inspectordataload', this.boundOnSvgDataLoad);
    }

    // React to external store changes (e.g. from the Reset button, or
    // filter changes made while viewing a different file).
    const store = useAllSpiceAppStore();

    // Flush any hidden IDs computed before mount. The hiddenDataIds watcher
    // skips store writes when the component isn't in the DOM yet
    // (document.contains check), so IDs from buildFromGroupedChanges
    // (which runs during created via the immediate watcher) need an
    // explicit push once we're mounted.
    if (store && this.sidebar && this.hiddenDataIds.size > 0) {
      store.diffFilterHiddenIds.value = Array.from(this.hiddenDataIds);
    }

    if (store?.diffFilterConfig) {
      this.stopFilterOverridesWatch = watch(() => store.diffFilterConfig.filterOverrides.value, (newOverrides) => {
        if (this.filterList.length === 0) return;

        if (Object.keys(newOverrides).length === 0) {
          this.resetFiltersToDefaults();
        } else {
          // Overrides changed while this instance may have been detached
          // (user changed filters on another file). Re-apply so the tree
          // is up-to-date when re-attached.
          this.applyStoredOverrides();
        }
      });
    }
  },

  beforeUnmount() {
    if (this.diffFileBox) {
      this.diffFileBox.removeEventListener('inspectordataload', this.boundOnInspectorDataLoad);
      this.diffFileBox.removeEventListener('diff-state-resync', this.boundOnResync);
    }
    if (this.stopFilterOverridesWatch) {
      this.stopFilterOverridesWatch();
    }
    if (this.boundOnSvgDataLoad) {
      document.removeEventListener('inspectordataload', this.boundOnSvgDataLoad);
      this.boundOnSvgDataLoad = null;
    }
  },

  methods: {
    /**
     * Formats a group label with count, e.g. "components (24/38)" when some
     * items are hidden by the filter, or "components (38)" when all are visible.
     */
    formatGroupLabel(groupName: string, fallbackCount: number): string {
      const total = this.filteredGroupCounts?.[groupName] ?? fallbackCount;
      const visible = this.visibleGroupCounts?.[groupName] ?? (this.visibleGroupCounts ? 0 : total);
      const displayName = formatGroupName(this.schemaType, groupName);
      if (visible < total) {
        return `${displayName} (${visible}/${total})`;
      }
      return `${displayName} (${total})`;
    },

    /**
     * Re-computes group labels when filtered/visible counts change.
     */
    updateGroupLabels(): void {
      if (this.filterList.length === 0) return;
      for (const group of this.filterList) {
        const totalEntities = this.attributesToDataIds[group.id]?.length ?? 0;
        group.name = this.formatGroupLabel(group.id, totalEntities);
      }
    },

    /**
     * Builds the filter tree from GroupedChanges (same grouping the Diff List uses).
     * Used in sidebar mode so that filter groups match diff list groups exactly.
     */
    buildFromGroupedChanges(): void {
      if (!this.groupedChanges) return;

      const startTime = performance.now();
      this.isLoading = true;

      const {attributesToDataIds, dataIdsToAttributes, filterList} = buildGroupedChangesFilterTree(
        this.groupedChanges,
        this.formatGroupLabel,
      );

      // Preserve order from groupedChanges (matches the diff list rendering).

      this.attributesToDataIds = attributesToDataIds;
      this.dataIdsToAttributes = dataIdsToAttributes;
      this.filterList = filterList;
      this.applyStoredOverrides();
      this.isLoading = false;

      timing('diff_filter_build', performance.now() - startTime, {
        schemaType: this.schemaType,
        source: 'groupedChanges',
      });
    },

    /**
     * Called when this component's DOM is re-attached after navigation.
     * In the project view, FileDiffPreview saves and reuses DOM trees,
     * so this component is NOT remounted — we must re-apply stored
     * overrides and sync hiddenDataIds to the store.
     */
    onResync(): void {
      if (this.filterList.length === 0) return;
      this.applyStoredOverrides();

      const store = useAllSpiceAppStore();
      if (store) {
        store.diffFilterHiddenIds.value = Array.from(this.hiddenDataIds);
      }
    },

    /**
     * Toolbar mode: receives diff data via DOM event.
     * @param {CustomEvent} event
     */
    onInspectorDataLoad(event: Event): void {
      event.preventDefault();
      const {detail: {baseContents, headContents}} = event as CustomEvent<{
        baseContents: DiffJsonNode;
        headContents: DiffJsonNode;
      }>;
      this.loadFilterData(headContents, baseContents);
    },

    /**
     * Sidebar mode: handles JSON data that may contain pages.
     * In the sidebar, we receive the entire document JSON via props,
     * so we need to unwrap pages here.
     * @param {Object|null} headJson
     * @param {Object|null} baseJson
     */
    loadFilterDataFromJson(headJson: DiffJson | null, baseJson: DiffJson | null): void {
      const headPages = headJson?.pages;
      const basePages = baseJson?.pages;

      if (headPages || basePages) {
        const pageCount = Math.max(headPages?.length ?? 0, basePages?.length ?? 0);
        for (let i = 0; i < pageCount; i++) {
          this.loadFilterData(headPages?.[i] ?? null, basePages?.[i] ?? null);
        }
      } else {
        this.loadFilterData(headJson, baseJson);
      }
    },

    /**
     * Processes diff data and builds the filter tree.
     * @param {Object|null} headContents
     * @param {Object|null} baseContents
     */
    loadFilterData(headContents: DiffJsonNode, baseContents: DiffJsonNode): void {
      if (this.headSize > FRONTEND_RENDER_THRESHOLD) {
        return;
      }

      const startTime = performance.now();
      this.isLoading = true;

      // Caching to avoid parsing the same file multiple times
      const pageId = getStringProperty(headContents, 'id') ?? getStringProperty(baseContents, 'id');
      if (pageId && this.filesParsed.has(pageId)) {
        this.isLoading = false;
        return;
      }
      if (pageId) {
        this.filesParsed.add(pageId);
      }

      try {
        const {dataIdsToAttributes, attributesToDataIds} = buildFilterDataFromContents(headContents, baseContents, this.attributesToDataIds);
        this.attributesToDataIds = Object.assign(this.attributesToDataIds, attributesToDataIds);
        this.dataIdsToAttributes = Object.assign(this.dataIdsToAttributes, dataIdsToAttributes);
      } catch (err) {
        console.error(err);
        this.emptyMessage = '(Unable to show filters)';
      }

      this.filterList = buildAttributeTree(Object.keys(this.attributesToDataIds), this.attributesToDataIds);
      this.applyStoredOverrides();
      this.isLoading = false;

      const schemaType = determineSchemaType(headContents, baseContents);
      timing('diff_filter_build', performance.now() - startTime, {
        schemaType,
      });
    },

    /**
     * Resets all filter items to visible, clears hidden data IDs, and
     * shows all SVG elements.  Used by the reset watcher and Select All.
     * @private
     */
    resetFiltersToDefaults(): void {
      setVisibilityRecursively(this.filterList, true);
      this.hiddenDataIds = new Set<string>();
      this.applyDisplayStyle(Object.keys(this.dataIdsToAttributes), true);
    },

    /**
     * @param {MouseEvent} event
     */
    onSelectAll(event: MouseEvent): void {
      event.preventDefault();
      this.resetFiltersToDefaults();
      this.persistCurrentOverrides();
    },

    /**
     * @param {MouseEvent} event
     */
    onSelectNone(event: MouseEvent): void {
      event.preventDefault();
      setVisibilityRecursively(this.filterList, false);
      const dataIds = Object.keys(this.dataIdsToAttributes);
      this.hiddenDataIds = new Set(dataIds);
      this.applyDisplayStyle(dataIds, false);
      this.persistCurrentOverrides();
    },

    /**
     * @param {FilterItem} filterItem
     */
    onToggleExpand(filterItem: FilterItem): void {
      filterItem.isExpanded = !filterItem.isExpanded;
    },

    /**
     * @param {FilterItem} filterItem
     */
    onToggleVisible(filterItem: FilterItem): void {
      filterItem.toggleVisibility();
      let dataIds: string[] = [];

      const affectedDataIds = this.attributesToDataIds[filterItem.id] ?? [];
      if (filterItem.isVisible) {
        for (const dataId of affectedDataIds) {
          this.hiddenDataIds.delete(dataId);
        }
        dataIds = affectedDataIds;
      } else {
        // Elements are hidden if and only if all of their attributes are hidden.
        const toHideDataIds: string[] = [];
        const hiddenFilters = collectHiddenFilters(this.filterList, filterItem.buildAncestorsChain());

        for (const dataId of affectedDataIds) {
          const dataIdChangedAttributes = this.dataIdsToAttributes[dataId] ?? [];
          if (dataIdChangedAttributes.every((attribute: string) => hiddenFilters.has(attribute))) {
            toHideDataIds.push(dataId);
          }
        }
        for (const dataId of toHideDataIds) {
          this.hiddenDataIds.add(dataId);
        }

        dataIds = toHideDataIds;
      }

      let parent = filterItem.parent;
      let forceIndeterminate = false;
      while (parent) {
        if (forceIndeterminate) {
          parent.isIndeterminate = true;
          parent.isVisible = false;
        } else {
          const parentDataIds = this.attributesToDataIds[parent.id] ?? [];
          const allSelected = parentDataIds.every((dataId: string) => !this.hiddenDataIds.has(dataId));
          const anySelected = parentDataIds.some((dataId: string) => !this.hiddenDataIds.has(dataId));
          forceIndeterminate = !allSelected && anySelected;
          parent.isIndeterminate = forceIndeterminate;
          parent.isVisible = allSelected;
        }
        parent = parent.parent;
      }

      if (dataIds.length > 0) {
        this.applyDisplayStyle(dataIds, filterItem.isVisible);
      }

      this.persistCurrentOverrides();
    },

    /**
     * Apply or remove hidden class from all elements in diff_from and diff_to svg with an attribute data-id that matches any of the dataIds.
     *
     * @private
     * @param {Array<string>} dataIds
     * @param {boolean} visible
     * @return {undefined}
     */
    applyDisplayStyle(dataIds: string[], visible: boolean): void {
      if (dataIds.length === 0) return;

      const startTime = performance.now();

      // Sidebar: query document-level (outside .diff-file-box).
      // Toolbar: scope to the closest .diff-file-box.
      const rootContainer = this.sidebar ? document : this.$el.closest('.diff-file-box');
      if (!rootContainer) return;

      const scopedDataIds = new Map<ParentNode, Set<string>>();
      for (const targetId of dataIds) {
        const {pageId, dataId} = parseDiffFilterTargetId(targetId);
        const pageContainer = this.sidebar && pageId ? findPageContainer(rootContainer, pageId) : null;
        const scopedContainer = pageContainer ?? rootContainer;

        if (!scopedDataIds.has(scopedContainer)) {
          scopedDataIds.set(scopedContainer, new Set<string>());
        }
        scopedDataIds.get(scopedContainer)!.add(dataId);
      }

      for (const [container, scopedIds] of scopedDataIds) {
        const selector = container === document ? '.diff-file-box .diff_from, .diff-file-box .diff_to' : '.diff_from, .diff_to';
        const diffSvgs = container.querySelectorAll(selector);
        const query = Array.from(scopedIds, (dataId: string) => `[data-id="${dataId}"]`).join(',');

        for (const diffSvg of diffSvgs) {
          const elements = diffSvg.querySelectorAll<SVGElement>(query);

          for (const element of elements) {
            if (visible) {
              element.style.removeProperty('visibility');
            } else {
              element.style.visibility = 'hidden';
            }
          }
        }
      }

      timing('diff_filter_apply', performance.now() - startTime, {
        elementCount: dataIds.length,
      });
    },

    /**
     * Applies stored filter overrides from the store to the current filter tree.
     * Called after building the filter tree in onInspectorDataLoad.
     *
     * When a parent has a `false` override, its `false` state cascades to any
     * children that don't have their own explicit override.  This lets a
     * category-level override (e.g. "components: false" set on a PcbDoc) apply
     * correctly even when the current file's tree has different child paths
     * (e.g. SchDoc children like "R1", "C1" instead of PCB children like
     * "pads", "tracks").
     *
     * Children that were explicitly re-enabled by the user are stored as
     * `true` overrides by persistCurrentOverrides(), so they survive the
     * cascade.
     */
    applyStoredOverrides(): void {
      const store = useAllSpiceAppStore();
      if (!store?.diffFilterConfig?.filterOverrides?.value) return;

      const overrides = store.diffFilterConfig.filterOverrides.value;
      if (Object.keys(overrides).length === 0) return;

      applyStoredFilterOverrides(this.filterList, overrides);
      this.hiddenDataIds = recalculateHiddenDataIds(this.filterList, this.dataIdsToAttributes);

      const allDataIds = Object.keys(this.dataIdsToAttributes);
      // First show all, then hide the hidden ones
      this.applyDisplayStyle(allDataIds, true);
      if (this.hiddenDataIds.size > 0) {
        this.applyDisplayStyle(Array.from(this.hiddenDataIds), false);
      }
    },

    /**
     * Persists the current filter overrides to the store and backend.
     * Merges with existing overrides so that paths from other file types
     * (e.g. "ports" in SchDoc when viewing a PcbDoc) are preserved.
     *
     * When a parent is hidden but a child is visible (user re-enabled it),
     * the child is stored as an explicit `true` override so that
     * applyStoredOverrides() won't cascade the parent's hidden state onto it.
     */
    persistCurrentOverrides(): void {
      const store = useAllSpiceAppStore();
      if (!store) return;

      store.diffFilterConfig.filterOverrides.value = buildStoredFilterOverrides(
        this.filterList,
        store.diffFilterConfig.filterOverrides.value,
      );
      saveDiffFilterState(store);
    },

  },
});
</script>

<template>
  <div :class="sidebar ? 'diff-filter-sidebar tw:px-2 tw:py-1' : 'allspice-diff-filter'">
    <!-- Sidebar mode -->
    <template v-if="sidebar">
      <div class="tw:flex tw:justify-between tw:items-center tw:mb-1">
        <span class="tw:font-semibold tw:text-sm">Filter by Group</span>
        <div class="tw:flex tw:gap-2 tw:text-xs">
          <button
            class="btn interact-fg"
            :class="filterList.length === 0 ? 'disabled' : ''"
            @click="onSelectAll($event)"
          >
            All
          </button>
          <button
            class="btn interact-fg"
            :class="filterList.length === 0 ? 'disabled' : ''"
            @click="onSelectNone($event)"
          >
            None
          </button>
        </div>
      </div>
      <div
        v-if="filterList.length === 0 && !isLoading"
        class="tw:text-sm tw:text-secondary"
      >
        {{ emptyMessage }}
      </div>
      <div v-if="isLoading">
        <span class="is-loading tw:h-8 tw:w-full tw:flex" />
      </div>
      <diff-filter-tree-item
        v-for="filterItem in filterList"
        :key="filterItem.id"
        :item="filterItem"
        @update:expanded="onToggleExpand($event)"
        @update:visible="onToggleVisible($event)"
      />
    </template>
    <!-- Toolbar mode (identical to main) -->
    <div
      v-else
      ref="dropdown"
      class="ui dropdown scrolling tiny basic button"
    >
      <span class="dropdown-title">
        Filter Diff
        <span
          v-if="hasActiveOverrides"
          class="tw:inline-block tw:w-2 tw:h-2 tw:rounded-full tw:bg-primary tw:ml-1"
          title="Custom filter settings are active"
        />
      </span>
      &nbsp;
      <svg-icon
        name="octicon-filter"
        :size="14"
        class-name="dropdown icon"
      />
      <div class="ui menu vertical hidden transition">
        <div
          class="item layer-toggle-item"
          :class="filterList.length === 0 ? 'disabled' : ''"
          @click="onSelectAll($event)"
        >
          Select All
        </div>
        <div
          class="item layer-toggle-item"
          :class="filterList.length === 0 ? 'disabled' : ''"
          @click="onSelectNone($event)"
        >
          Select None
        </div>
        <hr class="ui divider menu-item-divider">
        <div
          v-if="filterList.length === 0 && !isLoading"
          class="item disabled"
        >
          {{ emptyMessage }}
        </div>
        <div
          v-if="isLoading"
          class="item disabled"
        >
          <span class="is-loading tw:h-8 tw:w-full tw:flex" />
        </div>
        <diff-filter-tree-item
          v-for="filterItem in filterList"
          :key="filterItem.id"
          :item="filterItem"
          @update:expanded="onToggleExpand($event)"
          @update:visible="onToggleVisible($event)"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.diff-filter-sidebar {
  overflow-y: auto;
  max-height: 40vh;
  flex-shrink: 0;
}
</style>
